import { useMemo, useState } from 'react';
import { AlertTriangle, GripVertical } from 'lucide-react';
import { DAY_NAMES_SHORT, dayLabel } from './utils';

/**
 * Single-employee tile shown in the left panel.
 *
 * Visual states (mutually exclusive border palette):
 *  - Fully on leave (every day) → grey-out, drag disabled
 *  - Partial leave (1-6 days)   → light amber, draggable, "Ng İzin" badge
 *  - Borrowed (firm filter on, employee belongs elsewhere) → amber accent
 *  - Default                    → white/dark, hover-emerald
 *
 * Below the avatar row we render a 7-segment utilisation mini-bar:
 *  - Green segment = assigned that day. Click → smooth-scroll + flash that
 *    cell on the main board (`flashCell`).
 *  - Amber  segment = on leave that day (read-only).
 *  - Grey   segment = empty. Click → opens QuickAssign for SUPER_ADMIN
 *    (`onQuickAssignDay(date)` provided by parent).
 */
export function EmployeeTile({
  emp,
  bookedCount,
  onLeaveDays,
  summary,
  onDragStart,
  viewingFirmId,
  weekDates = [],
  onQuickAssignDay,
  isFocused = false,
  onToggleFocus,
  isSelected = false,
  onTapSelect,
}) {
  const [hovered, setHovered] = useState(false);

  // "Borrowed" = firm filter is active and this employee primarily belongs
  // elsewhere (and isn't natively eligible for the active firm).
  const isBorrowed =
    !!viewingFirmId &&
    emp.firm_id !== viewingFirmId &&
    !(emp.eligible_firm_ids || []).includes(viewingFirmId);

  // Build a {date -> {type, route_id?, ...}} map from the weekly summary.
  // dupCounts[date] tells us how many concurrent assignments exist for an
  // assigned segment so we can paint it red.
  const dayInfo = useMemo(() => {
    const m = {};
    for (const s of summary || []) {
      if (m[s.date]) {
        // If we already have an entry, merge: prefer leave > assigned for the
        // status, but keep the first route info for tooltips.
        m[s.date] = { ...m[s.date], ...s, doubleBooked: true, doubleCount: (m[s.date].doubleCount || 1) + 1 };
      } else {
        m[s.date] = s;
      }
    }
    return m;
  }, [summary]);
  const filledDays = (summary || []).filter((s) => s.type === 'assigned').length;
  const conflictDays = (summary || []).filter((s) => s.doubleBooked).length;
  const isFullyOnLeave = onLeaveDays >= 7;
  const isPartialLeave = onLeaveDays > 0 && onLeaveDays < 7;

  const flashCell = (info) => {
    if (!info || info.type !== 'assigned' || !info.route_id) return;
    const sel = `[data-testid="planning-cell-${info.route_id}-${info.date}"]`;
    const el = document.querySelector(sel);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
    el.classList.add('ring-2', 'ring-amber-400', 'ring-offset-1');
    setTimeout(() => el.classList.remove('ring-2', 'ring-amber-400', 'ring-offset-1'), 1500);
  };

  return (
    <div className="relative" onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
      <div
        draggable={!isFullyOnLeave}
        onDragStart={(e) => isFullyOnLeave ? e.preventDefault() : onDragStart(e, emp)}
        onDoubleClick={() => onToggleFocus && onToggleFocus(emp.id)}
        onClick={(e) => {
          // Tek tıklama = tap-to-pick seçim toggle. Çift tıklamayı bozmamak
          // için tile içindeki diğer interaktif elementler (mini-bar segment)
          // stopPropagation çağırıyor.
          if (isFullyOnLeave) return;
          if (!onTapSelect) return;
          // Drag başlamış mı kontrolü tarayıcı tarafından zaten ayrı event;
          // burada saf click event'i geldiyse seçim yap.
          onTapSelect(emp);
        }}
        className={`group flex flex-col gap-1 px-2 py-1.5 rounded-md border transition-colors ${
          isSelected
            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 ring-2 ring-blue-400 ring-offset-1 cursor-grab active:cursor-grabbing hover:border-blue-600'
            : isFocused
            ? 'border-rose-400 bg-rose-50 dark:bg-rose-900/20 ring-2 ring-rose-300 ring-offset-1 cursor-grab active:cursor-grabbing hover:border-rose-500'
            : conflictDays > 0
            ? 'border-red-300 bg-red-50/40 dark:bg-red-900/10 cursor-grab active:cursor-grabbing hover:border-red-400'
            : isFullyOnLeave
            ? 'border-amber-200 bg-amber-50 cursor-not-allowed opacity-70'
            : isPartialLeave
            ? 'border-amber-200 bg-amber-50/30 dark:bg-amber-900/10 cursor-grab active:cursor-grabbing hover:border-amber-300'
            : isBorrowed
            ? 'border-amber-300 bg-amber-50/50 dark:bg-amber-900/10 cursor-grab active:cursor-grabbing hover:border-amber-400'
            : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 cursor-grab active:cursor-grabbing hover:border-emerald-300 hover:bg-emerald-50/50 dark:hover:bg-emerald-900/10'
        }`}
        data-testid={`planning-emp-${emp.id}`}
        title={isSelected ? 'Seçili — boş hücreye dokunarak ata. Tekrar dokun → seçimi kaldır.' : isFocused ? 'Odak modu aktif — tahtada kırmızı vurgulu hücreler bu çalışana ait. Çift tıkla kapat.' : 'Tıkla → seç (mobilde boş hücreye dokunarak ata) · Çift tıkla → tahtada vurgula'}
      >
        <div className="flex items-center gap-2">
          <GripVertical className={`h-3.5 w-3.5 ${isFullyOnLeave ? 'text-amber-300' : 'text-slate-300 group-hover:text-emerald-400'}`} />
          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-400 to-blue-500 text-white text-[11px] font-bold flex items-center justify-center shrink-0">
            {emp.full_name?.charAt(0)?.toUpperCase() || '?'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-slate-700 dark:text-slate-200 truncate">{emp.full_name}</p>
            <p className="text-[10px] text-slate-400 truncate">{emp.firm_name || emp.depo || '—'}</p>
          </div>
          {isBorrowed && (
            <span
              className="text-[9px] px-1 py-0 rounded bg-amber-200 text-amber-900 font-bold uppercase border border-amber-300"
              title={`${emp.firm_name || 'Diğer firma'} çalışanı — ödünç olarak görüntüleniyor`}
              data-testid={`planning-emp-borrowed-${emp.id}`}
            >
              Ödünç
            </span>
          )}
          {(emp.eligible_firm_ids?.length || 0) > 1 && (
            <span
              className="text-[9px] px-1 py-0 rounded bg-purple-100 text-purple-700 font-bold uppercase"
              title={`Çoklu firma: ${emp.eligible_firm_ids.length}`}
            >
              ×{emp.eligible_firm_ids.length}
            </span>
          )}
          {emp.firm_name && !isBorrowed && (
            <span className="text-[9px] px-1 py-0 rounded bg-indigo-50 text-indigo-600 font-semibold uppercase tracking-tight border border-indigo-100">
              {emp.firm_name.slice(0, 4)}
            </span>
          )}
          {isFullyOnLeave && (
            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-200 text-amber-800 font-semibold">İzin</span>
          )}
          {isPartialLeave && (
            <span
              className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold border border-amber-200"
              title={`${onLeaveDays} gün izinli — diğer günlere atanabilir`}
              data-testid={`planning-emp-partial-leave-${emp.id}`}
            >
              {onLeaveDays}g İzin
            </span>
          )}
          {!isFullyOnLeave && bookedCount > 0 && (
            <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold ${conflictDays > 0 ? 'bg-red-100 text-red-700 border border-red-200' : 'bg-emerald-100 text-emerald-700'}`}
              title={conflictDays > 0 ? `${conflictDays} günde çift atama` : 'Atanmış gün sayısı'}
              data-testid={`planning-emp-bookcount-${emp.id}`}
            >
              {conflictDays > 0 ? `${bookedCount}!` : bookedCount}
            </span>
          )}
        </div>

        {/* Mini-bar: 7 daily segments + counter. */}
        {weekDates.length === 7 && (
          <div
            className="flex gap-[2px] mt-0.5"
            title={weekDates.map((d, i) => {
              const t = dayInfo[d]?.type;
              const code = dayInfo[d]?.route_code;
              return `${DAY_NAMES_SHORT[i]}: ${t === 'assigned' ? `${code || 'Atanmış'}` : t === 'leave' ? 'İzinli' : 'Boş'}`;
            }).join(' · ')}
            data-testid={`planning-emp-bar-${emp.id}`}
          >
            {weekDates.map((d, i) => {
              const info = dayInfo[d];
              const st = info?.type;
              const isDouble = !!info?.doubleBooked;
              const cls =
                st === 'assigned' && isDouble
                  ? 'bg-red-500 hover:bg-red-600 cursor-pointer'
                  : st === 'assigned'
                  ? 'bg-emerald-400 hover:bg-emerald-500 cursor-pointer'
                  : st === 'leave'
                  ? 'bg-amber-400'
                  : 'bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600';
              const isAssigned = st === 'assigned' && info?.route_id;
              const isEmpty = !st;
              const isClickable = isAssigned || (isEmpty && !!onQuickAssignDay);
              return (
                <div
                  key={d}
                  className={`flex-1 h-1.5 rounded-sm relative overflow-hidden ${isClickable ? 'cursor-pointer' : ''}`}
                  onClick={(e) => {
                    if (!isClickable) return;
                    e.stopPropagation();
                    if (isAssigned) flashCell(info);
                    else if (isEmpty && onQuickAssignDay) onQuickAssignDay(d);
                  }}
                  title={
                    isAssigned && isDouble
                      ? `${DAY_NAMES_SHORT[i]}: ${info.route_code || 'Atanmış'} — ÇİFT ATAMA (${info.doubleCount || 2}×)`
                      : isAssigned
                      ? `${DAY_NAMES_SHORT[i]}: ${info.route_code || 'Atanmış'} — tıkla`
                      : isEmpty && onQuickAssignDay
                      ? `${DAY_NAMES_SHORT[i]}: Boş — tıklayarak ata`
                      : st === 'leave'
                      ? `${DAY_NAMES_SHORT[i]}: İzinli`
                      : DAY_NAMES_SHORT[i]
                  }
                  data-testid={`planning-emp-seg-${emp.id}-${i}`}
                >
                  <div className={`absolute inset-0 ${cls}`} />
                </div>
              );
            })}
            <span className="text-[9px] text-slate-400 font-mono ml-1 shrink-0" data-testid={`planning-emp-fill-${emp.id}`}>
              {filledDays}/7
            </span>
          </div>
        )}
      </div>

      {/* Hover popover with the full day-by-day breakdown. */}
      {hovered && summary.length > 0 && (
        <div
          className="absolute left-full top-0 ml-2 z-50 w-60 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl p-2 pointer-events-none"
          data-testid={`planning-emp-summary-${emp.id}`}
        >
          <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1 px-1">Haftalık Özet</div>
          <ul className="space-y-0.5 max-h-56 overflow-y-auto">
            {summary.map((s, idx) => (
              <li key={idx} className="flex items-center gap-1.5 text-[11px] px-1 py-0.5 rounded">
                <span className="font-semibold text-slate-500 w-7">{dayLabel(s.date)}</span>
                <span className="text-slate-400 w-12">{s.date.slice(5)}</span>
                {s.type === 'leave' ? (
                  <span className="text-amber-600 inline-flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" /> İzinli
                  </span>
                ) : (
                  <span className="flex-1 truncate">
                    <span className="text-emerald-600 mr-1">{s.route_code}</span>
                    <span className="text-slate-400">{s.shift_name || ''}</span>
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
